import React, { useEffect, useState } from "react";
import api from "../services/api";
import { qlAlert } from "../utils/dialogs";
import downloadFile from "../services/downloadFile";
import { imprimerDocument } from "../services/imprimer";

const pct = value => `${Number(value || 0).toFixed(2)} %`;

function pdfOpen(blob, filename) {
  return downloadFile({
    url: `${api.defaults.baseURL}generated/${encodeURIComponent(filename)}`,
    filename,
    mimeType: "application/pdf",
    data: blob,
  }).then(result => {
    if (!result?.cancelled) qlAlert("PDF téléchargé avec succès.", "success");
    return result;
  }).catch(() => qlAlert("Téléchargement du PDF impossible.", "error"));
}

function pdfPrint(blob) {
  return imprimerDocument({ data: blob, filename: "classement.pdf" });
}

function PrintButtons({ pdfUrl, filename }) {
  const [loading, setLoading] = useState(null);
  const getPdf = () => api.get(pdfUrl, { responseType: "blob" }).then(r => r.data);
  const download = () => { setLoading("dl"); getPdf().then(blob => pdfOpen(blob, filename)).catch(() => qlAlert("PDF indisponible.", "error")).finally(() => setLoading(null)); };
  const print = () => { setLoading("pr"); getPdf().then(blob => pdfPrint(blob)).catch(() => qlAlert("PDF indisponible.", "error")).finally(() => setLoading(null)); };
  return <div className="d-flex gap-2 mb-3">
    <button className="btn btn-outline-primary btn-sm" onClick={download} disabled={!!loading}>
      {loading === "dl" ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-download me-1"></i>}
      Télécharger PDF
    </button>
    <button className="btn btn-outline-primary btn-sm" onClick={print} disabled={!!loading}>
      {loading === "pr" ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-printer me-1"></i>}
      Imprimer
    </button>
  </div>;
}

const emptyFormula = {
  name: "", period_start: "", period_end: "",
  activity_weight: 20, results_weight: 50, progress_weight: 20, referral_weight: 10,
  minimum_active_days: 2, minimum_distinct_results: 2, daily_minutes_cap: 120,
};

function StudentRankingTab() {
  const [state, setState] = useState({ configs: [], runs: [] });
  const [choice, setChoice] = useState({ config_id: "" });
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState("");
  const [showFormulaForm, setShowFormulaForm] = useState(false);
  const [formula, setFormula] = useState(emptyFormula);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("student-ranking/runs/").then(r => setState(r.data)).catch(e => setError(
    e?.response?.status === 403 ? "Vous n’avez pas la permission explicite de gérer les classements." : "Classement indisponible."
  ));
  // useEffect(load, []) passerait directement la Promise renvoyée par load() en
  // guise de fonction de nettoyage : React l'appelle sans vérifier son type au
  // démontage (changement d'onglet) et plante toute la page ("n is not a
  // function"). Le wrapper évite que l'effet retourne autre chose qu'undefined.
  useEffect(() => { load(); }, []);

  const weightSum = ["activity_weight", "results_weight", "progress_weight", "referral_weight"]
    .reduce((sum, key) => sum + Number(formula[key] || 0), 0);

  const createFormula = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("student-ranking/configs/", formula);
      setShowFormulaForm(false);
      setFormula(emptyFormula);
      await load();
    } catch (err) {
      qlAlert(err?.response?.data?.detail || "Création de la formule impossible.", "error");
    } finally {
      setBusy(false);
    }
  };

  const calculate = async () => {
    setError("");
    try { await api.post("student-ranking/runs/", choice); setSelected([]); await load(); }
    catch (e) { setError(e?.response?.data?.detail || "Calcul impossible."); }
  };
  const run = state.runs[0];
  const toggle = id => setSelected(current => current.includes(id) ? current.filter(x => x !== id) : current.length < 5 ? [...current, id] : current);
  const saveSelection = async () => {
    if (!run) return;
    try { await api.post(`student-ranking/runs/${run.id}/selections/`, { student_ids: selected, reason: "" }); setSelected([]); await load(); }
    catch (e) { qlAlert(e?.response?.data?.detail || "Sélection impossible.", "error"); }
  };
  const latestSelection = run?.selections?.length ? run.selections[run.selections.length - 1] : null;
  const officialize = async () => {
    if (!latestSelection) return;
    try { await api.post(`student-ranking/selections/${latestSelection.id}/officialize/`); await load(); }
    catch (e) { qlAlert(e?.response?.data?.detail || "Officialisation impossible.", "error"); }
  };

  return <>
    <div className="ap-page-header">
      <div>
        <p className="ap-page-desc">Créez une formule, calculez le classement de tous les élèves de la plateforme (aucune distinction de classe ni de série), sélectionnez jusqu’à cinq lauréats puis officialisez pour les notifier.</p>
      </div>
      <button className="btn btn-primary btn-sm" onClick={() => setShowFormulaForm(true)}><i className="bi bi-plus-lg me-1"></i>Nouvelle formule</button>
    </div>
    {error && <div className="alert alert-warning" role="alert">{error}</div>}

    <h2>Mes formules</h2>
    {state.configs.length === 0 ? <p className="text-muted">Aucune formule créée pour l’instant.</p> : (
      <div className="pt-directory">
        {state.configs.map(c => (
          <article key={c.id} className="pt-teacher-card ap-referral-card">
            <div className="pt-avatar"><i className="bi bi-trophy"></i></div>
            <div>
              <h3>{c.name}</h3>
              <p>{c.period_start} → {c.period_end}</p>
              <p className="text-muted small">Activité {c.activity_weight}% · Résultats {c.results_weight}% · Progression {c.progress_weight}% · Parrainage {c.referral_weight}%</p>
              <p className="text-muted small">Min. {c.minimum_active_days} j actifs, {c.minimum_distinct_results} résultats distincts</p>
            </div>
          </article>
        ))}
      </div>
    )}

    <h2>Calculer un classement</h2>
    <div className="ranking-controls">
      <select value={choice.config_id} onChange={e => setChoice({ config_id: e.target.value })}><option value="">Formule</option>{state.configs.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
      <button type="button" disabled={!choice.config_id} onClick={calculate}>Calculer</button>
    </div>

    {run && <>
      <h2>Classement de tous les élèves <span className={`badge bg-${run.status === "validated" ? "success" : "secondary"}`}>{run.status === "validated" ? "Validé" : "Brouillon"}</span></h2>
      <p className="small">Pondérations : activité {run.formula.weights.activity} %, résultats {run.formula.weights.results} %, progression {run.formula.weights.progress} %, parrainage {run.formula.weights.referral ?? 0} %.</p>
      <PrintButtons pdfUrl={`student-ranking/runs/${run.id}/pdf/`} filename="classement_eleves_top5.pdf" />
      <div className="ranking-table-wrap"><table className="ranking-table"><caption className="visually-hidden">Classement complet de tous les élèves, du premier au dernier</caption><thead><tr><th scope="col">Choix</th><th scope="col">Rang</th><th scope="col">Élève</th><th scope="col">Activité</th><th scope="col">Résultats</th><th scope="col">Progression</th><th scope="col">Parrainage</th><th scope="col">Total</th><th scope="col">Détail</th></tr></thead><tbody>
        {run.entries.map(entry => <tr key={entry.id} className={!entry.sufficient_data ? "insufficient" : ""}>
          <td><input type="checkbox" aria-label={`Sélectionner ${entry.student_name}`} disabled={!entry.sufficient_data} checked={selected.includes(entry.student_id)} onChange={() => toggle(entry.student_id)} /></td>
          <td>{entry.rank || "—"}</td>
          <td>{entry.student_name} <strong>— {entry.total_score_20}/20</strong></td>
          <td>{pct(entry.activity_score)}</td>
          <td>{pct(entry.results_score)}</td>
          <td>{pct(entry.progress_score)}</td>
          <td>{pct(entry.referral_score)}</td>
          <td><strong>{pct(entry.total_score)}</strong></td>
          <td>{entry.sufficient_data ? `${entry.detail.active_days} jours, ${entry.detail.distinct_results} résultats distincts, ${entry.detail.referrals_validated || 0} parrainage(s)` : "Données insuffisantes"}</td>
        </tr>)}
      </tbody></table></div>
      <button type="button" disabled={!selected.length} onClick={saveSelection}>Enregistrer jusqu’à cinq lauréats</button>
      {latestSelection && (
        latestSelection.is_official
          ? <p className="ap-referral-days"><i className="bi bi-check-circle-fill"></i> Sélection officialisée — les lauréats ont été notifiés.</p>
          : <button type="button" className="btn btn-sm btn-success ms-2" onClick={officialize}>Officialiser et notifier les lauréats</button>
      )}
    </>}

    {showFormulaForm && (
      <div className="ap-modal-overlay" onClick={() => setShowFormulaForm(false)}>
        <div className="ap-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
          <div className="ap-modal-header"><strong className="ap-modal-title">Nouvelle formule de classement</strong><button className="ap-modal-close" onClick={() => setShowFormulaForm(false)}>×</button></div>
          <form className="pt-profile-form" style={{ padding: "1.2rem" }} onSubmit={createFormula}>
            <input required placeholder="Nom de la formule" value={formula.name} onChange={e => setFormula(v => ({ ...v, name: e.target.value }))} style={{ gridColumn: "1/-1" }} />
            <label className="ranking-form-field">Début de période<input required type="date" value={formula.period_start} onChange={e => setFormula(v => ({ ...v, period_start: e.target.value }))} /></label>
            <label className="ranking-form-field">Fin de période<input required type="date" value={formula.period_end} onChange={e => setFormula(v => ({ ...v, period_end: e.target.value }))} /></label>
            <label className="ranking-form-field">Poids activité (%)<input required type="number" min="0" max="100" value={formula.activity_weight} onChange={e => setFormula(v => ({ ...v, activity_weight: e.target.value }))} /></label>
            <label className="ranking-form-field">Poids résultats (%)<input required type="number" min="0" max="100" value={formula.results_weight} onChange={e => setFormula(v => ({ ...v, results_weight: e.target.value }))} /></label>
            <label className="ranking-form-field">Poids progression (%)<input required type="number" min="0" max="100" value={formula.progress_weight} onChange={e => setFormula(v => ({ ...v, progress_weight: e.target.value }))} /></label>
            <label className="ranking-form-field">Poids parrainage (%)<input required type="number" min="0" max="100" value={formula.referral_weight} onChange={e => setFormula(v => ({ ...v, referral_weight: e.target.value }))} /></label>
            <p className={`small ${weightSum === 100 ? "text-success" : "text-danger"}`} style={{ gridColumn: "1/-1" }}>Total des pondérations : {weightSum} % {weightSum === 100 ? "✓" : "(doit faire 100 %)"}</p>
            <label className="ranking-form-field">Jours actifs minimum<input required type="number" min="1" value={formula.minimum_active_days} onChange={e => setFormula(v => ({ ...v, minimum_active_days: e.target.value }))} /></label>
            <label className="ranking-form-field">Résultats distincts minimum<input required type="number" min="1" value={formula.minimum_distinct_results} onChange={e => setFormula(v => ({ ...v, minimum_distinct_results: e.target.value }))} /></label>
            <label className="ranking-form-field">Plafond minutes/jour<input required type="number" min="1" value={formula.daily_minutes_cap} onChange={e => setFormula(v => ({ ...v, daily_minutes_cap: e.target.value }))} /></label>
            <button className="btn btn-primary" style={{ gridColumn: "1/-1" }} disabled={busy || weightSum !== 100}>Créer la formule</button>
          </form>
        </div>
      </div>
    )}
  </>;
}

function GeneratorRankingTab() {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("student-ranking/generators/").then(r => setEntries(r.data.entries)).catch(e => setError(
      e?.response?.status === 403 ? "Vous n’avez pas la permission explicite de gérer les classements." : (e?.response?.data?.detail || "Classement indisponible.")
    ));
  }, []);
  return <>
    <p className="ap-page-desc">Générateurs d’épreuves classés par nombre total d’épreuves générées.</p>
    {error && <div className="alert alert-warning">{error}</div>}
    {!error && <PrintButtons pdfUrl="student-ranking/generators/pdf/" filename="classement_generateurs.pdf" />}
    {!entries ? <p className="text-muted">Chargement…</p> : entries.length === 0 ? <div className="alert alert-info">Aucun générateur pour l’instant.</div> : (
      <div className="ranking-table-wrap"><table className="ranking-table"><thead><tr><th scope="col">Rang</th><th scope="col">Générateur</th><th scope="col">Épreuves générées</th></tr></thead><tbody>
        {entries.map(e => <tr key={e.user_id}><td>{e.rank}</td><td>{e.name}</td><td><strong>{e.epreuves_generees}</strong></td></tr>)}
      </tbody></table></div>
    )}
  </>;
}

function StaffRankingTab() {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("student-ranking/staff/").then(r => setEntries(r.data.entries)).catch(e => setError(
      e?.response?.status === 403 ? "Vous n’avez pas la permission explicite de gérer les classements." : (e?.response?.data?.detail || "Classement indisponible.")
    ));
  }, []);
  return <>
    <p className="ap-page-desc">Enseignants de l’équipe (comptes internes) classés par durée de connexion cumulée, nombre d’assistances validées et contenus enregistrés.</p>
    {error && <div className="alert alert-warning">{error}</div>}
    {!error && <PrintButtons pdfUrl="student-ranking/staff/pdf/" filename="classement_enseignants_top5.pdf" />}
    {!entries ? <p className="text-muted">Chargement…</p> : entries.length === 0 ? <div className="alert alert-info">Aucun enseignant pour l’instant.</div> : (
      <div className="ranking-table-wrap"><table className="ranking-table"><thead><tr><th scope="col">Rang</th><th scope="col">Enseignant</th><th scope="col">Connexion (min)</th><th scope="col">Assistances validées</th><th scope="col">Contenus enregistrés</th><th scope="col">Score</th></tr></thead><tbody>
        {entries.map(e => <tr key={e.user_id}><td>{e.rank}</td><td>{e.name}</td><td>{e.connection_minutes}</td><td>{e.assistance_validated}</td><td>{e.content_count}</td><td><strong>{e.score}</strong></td></tr>)}
      </tbody></table></div>
    )}
  </>;
}

const TABS = { student: "Élèves à primer", generateur: "Générateurs", enseignant: "Enseignants" };

export default function StudentRankingAdmin() {
  const [tab, setTab] = useState("student");
  return <div className="ap-page">
    <div className="ap-page-header">
      <div><h1 className="ap-page-title">Classements</h1></div>
    </div>
    <div className="ranking-tabs">
      {Object.entries(TABS).map(([key, label]) => (
        <button key={key} type="button" className={`btn btn-sm ${tab === key ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setTab(key)}>{label}</button>
      ))}
    </div>
    {tab === "student" && <StudentRankingTab />}
    {tab === "generateur" && <GeneratorRankingTab />}
    {tab === "enseignant" && <StaffRankingTab />}
  </div>;
}
